// src/components/GoogleOneTap.js
import React, { useEffect, useCallback, useState, useRef } from 'react';
import { jwtDecode } from 'jwt-decode';
import { userLogger } from '../utils/userLogger';
import { useIsClient } from '../utils/hydrationSafe';
import { useUser } from '../context/UserContext';
import {
  getExpectedGoogleClientId,
  getStoredGoogleIdToken,
  markGsiOriginBlocked,
} from '../utils/googleAuth';
import './GoogleOneTap.css';

const GSI_INIT_FLAG = '__originselectGsiInitialized';
const GSI_CLIENT_ID_KEY = '__originselectGsiClientId';

/**
 * Google One Tap Sign-In Component
 * Displays automatic sign-in prompt for users logged into Chrome with Google account
 * 
 * Features:
 * - Non-intrusive popup (bottom-right corner)
 * - Automatic detection of Google login status
 * - Remembers user dismissal (won't show again for 24 hours if dismissed)
 * - Only shows on homepage and key landing pages
 * - Hydration-safe (no SSR issues)
 */
function GoogleOneTap({ 
  onSuccess, 
  onError,
  disabled = false,
  showOnAllPages = false // Set to true to show on all pages, false for selective pages
}) {
  const isClient = useIsClient();
  const [initialized, setInitialized] = useState(false);
  const [initializing, setInitializing] = useState(false);
  const initializingRef = useRef(false);
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const { setUser } = useUser(); // Get setUser from UserContext

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
  }, [onSuccess, onError]);

  // Add mount log
  useEffect(() => {
    console.log('🎯 GoogleOneTap component mounted!', {
      isClient,
      disabled,
      showOnAllPages,
      pathname: typeof window !== 'undefined' ? window.location.pathname : 'SSR'
    });
  }, []);

  // Pages where One Tap should appear (if showOnAllPages is false)
  const shouldShowOnCurrentPage = useCallback(() => {
    if (!isClient) return false;
    if (showOnAllPages) return true;
    
    const path = window.location.pathname;
    
    // Show on homepage and key landing pages
    const allowedPages = [
      '/',
      '/country/canada',
      '/global',
      '/global/home',
      '/country/canada/products',
      '/global/products',
    ];
    
    return allowedPages.includes(path);
  }, [isClient, showOnAllPages]);

  const handleCredentialResponse = useCallback((response) => {
    try {
      // Decode the JWT token
      const userObject = jwtDecode(response.credential);
      
      // Structured logging
      console.log("🔐 ONE_TAP_LOGIN", {
        timestamp: new Date().toISOString(),
        email: userObject.email,
        name: userObject.name,
        userId: userObject.sub,
        domain: userObject.email.split('@')[1],
        loginMethod: 'Google One Tap',
        sessionId: Date.now().toString(36) + Math.random().toString(36).substr(2),
        page: window.location.pathname
      });
      
      // Store user data
      const userData = {
        name: userObject.name,
        email: userObject.email,
        picture: userObject.picture,
        sub: userObject.sub
      };
      
      // Save to localStorage
      localStorage.setItem('userToken', response.credential);
      localStorage.setItem('userIdToken', response.credential);
      localStorage.setItem('userData', JSON.stringify(userData));
      localStorage.setItem('userSignInMethod', 'Google One Tap');
      localStorage.setItem('userSignInTime', new Date().toISOString());
      localStorage.setItem('userLastActivity', Date.now().toString());
      
      // ✅ UPDATE USER CONTEXT (so NavBar shows logged-in state)
      setUser(userData);
      
      // Log user sign-in
      userLogger.logSignIn({
        email: userObject.email,
        name: userObject.name,
        picture: userObject.picture,
        method: 'One Tap'
      });
      
      // 📧 SEND TO BACKEND FOR EMAIL COLLECTION
      // Call your backend API to save email for marketing
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://d3d8378t728e1f.cloudfront.net';
      fetch(`${apiBaseUrl}/api/users/signin-track`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: userObject.email,
          name: userObject.name,
          picture: userObject.picture,
          signInMethod: 'Google One Tap',
          signInPage: window.location.pathname,
          timestamp: new Date().toISOString()
        })
      })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log('✅ User sign-in tracked on backend:', data.message);
        }
      })
      .catch(error => {
        // Silent fail - don't break user experience if backend is down
        console.log('⚠️ Backend tracking failed (non-critical):', error.message);
      });
      
      // === ENHANCED ANALYTICS TRACKING ===
      
      // 1. Set user ID for Google Analytics (for audience building)
      if (window.gtag) {
        // Set user ID and properties for GA4
        window.gtag('config', 'G-GBHGGRPFVR', {
          user_id: userObject.sub, // Use Google's unique identifier
          user_properties: {
            signup_method: 'Google One Tap',
            email_domain: userObject.email.split('@')[1],
            signin_page: window.location.pathname
          }
        });
        
        // 2. Fire standard login event (required for Google's audience building)
        window.gtag('event', 'login', {
          method: 'Google One Tap',
          event_category: 'Authentication',
          event_label: 'Google One Tap Success',
          page_location: window.location.href,
          page_path: window.location.pathname
        });
        
        // 3. Custom event for One Tap specific tracking
        window.gtag('event', 'one_tap_signin_success', {
          event_category: 'User Acquisition',
          event_label: window.location.pathname,
          user_email_domain: userObject.email.split('@')[1],
          signin_context: 'automatic_popup'
        });
        
        // 4. Track for audience segmentation
        window.gtag('event', 'user_engagement', {
          engagement_type: 'account_created',
          method: 'Google One Tap',
          event_category: 'Engagement',
          event_label: 'New User Sign In'
        });
      }
      
      // 5. Track for PostHog/Segment integration (if you add it later)
      if (window.posthog) {
        window.posthog.identify(userObject.sub, {
          email: userObject.email,
          name: userObject.name,
          signup_method: 'Google One Tap',
          signin_page: window.location.pathname
        });
        window.posthog.capture('user_signed_in', {
          method: 'Google One Tap',
          page: window.location.pathname
        });
      }
      
      // 6. Track for Segment analytics (if you add it later)
      if (window.analytics) {
        window.analytics.identify(userObject.sub, {
          email: userObject.email,
          name: userObject.name,
          signupMethod: 'Google One Tap',
          signinPage: window.location.pathname
        });
        window.analytics.track('User Signed In', {
          method: 'Google One Tap',
          page: window.location.pathname
        });
      }
      
      // Call parent callback if provided
      if (onSuccessRef.current) {
        onSuccessRef.current(userData);
      }
      
    } catch (error) {
      console.error("Error processing One Tap credential:", error);
      
      // Track login failure with details
      if (window.gtag) {
        window.gtag('event', 'login_error', {
          method: 'Google One Tap',
          event_category: 'Authentication',
          event_label: 'One Tap Decode Error',
          error_message: error.message,
          page_path: window.location.pathname
        });
        
        window.gtag('event', 'exception', {
          description: `One Tap Login Error: ${error.message}`,
          fatal: false
        });
      }
      
      if (onErrorRef.current) {
        onErrorRef.current(error);
      }
    }
  }, [setUser]);

  useEffect(() => {
    const isUnsupportedDevOrigin =
      isClient &&
      typeof window !== 'undefined' &&
      process.env.NODE_ENV === 'development' &&
      (window.location.hostname === '127.0.0.1' || window.location.hostname === 'localhost');

    if (!isClient || disabled || initialized || initializing || !shouldShowOnCurrentPage() || isUnsupportedDevOrigin) {
      console.log('🔍 GoogleOneTap check:', {
        isClient,
        disabled,
        initialized,
        initializing,
        isUnsupportedDevOrigin,
        shouldShow: shouldShowOnCurrentPage(),
        currentPath: isClient ? window.location.pathname : 'N/A'
      });

      if (isUnsupportedDevOrigin) {
        console.warn('⚠️ Google One Tap disabled on localhost/127.0.0.1 in development because this origin is not authorized for the active Google client ID. Use the production domain or update Google OAuth origins before testing sign-in here.');
      }
      return;
    }

    // Check if user is already logged in with valid session
    const existingToken = getStoredGoogleIdToken();
    
    // If token exists, consider the session valid (don't clear on inactivity)
    // Google tokens have their own expiration; we trust them
    if (existingToken) {
      console.log("✅ User already logged in with valid token, skipping One Tap");
      return;
    }

    console.log('🚀 Starting Google One Tap initialization...');

    // Initialize Google One Tap
    const initializeOneTap = () => {
      if (initializingRef.current) return;
      initializingRef.current = true;
      setInitializing(true);

      if (!window.google?.accounts?.id) {
        console.log("⏳ Google Identity Services not loaded yet, retrying...");
        initializingRef.current = false;
        setInitializing(false);
        setTimeout(initializeOneTap, 100);
        return;
      }

      const clientId = getExpectedGoogleClientId();
      if (!clientId) {
        console.error("❌ Google Client ID not found in environment variables");
        initializingRef.current = false;
        setInitializing(false);
        return;
      }

      console.log('✅ Google Identity Services loaded, client ID found');

      try {
        const alreadyInitialized = window[GSI_INIT_FLAG] === true;
        const initializedClientId = window[GSI_CLIENT_ID_KEY];

        if (!alreadyInitialized || initializedClientId !== clientId) {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: handleCredentialResponse,
            auto_select: false, // Show popup instead of silent auto-login
            cancel_on_tap_outside: false, // Keep popup visible until user interacts
            context: 'signin', // Context: 'signin', 'signup', or 'use'
            itp_support: true, // Enable Intelligent Tracking Prevention support
          });
          window[GSI_INIT_FLAG] = true;
          window[GSI_CLIENT_ID_KEY] = clientId;
        } else {
          console.log('ℹ️ Google One Tap already initialized for this client ID; skipping duplicate initialize call.');
        }

        console.log('✅ Google One Tap initialized, calling prompt...');

        // Display the One Tap prompt
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            const reason = notification.getNotDisplayedReason();
            console.log('One Tap not displayed:', reason);

            if (reason === 'unregistered_origin') {
              markGsiOriginBlocked();
            }
            
            // Track non-display with detailed reasons
            if (window.gtag) {
              window.gtag('event', 'one_tap_not_displayed', {
                event_category: 'Authentication',
                event_label: reason,
                page_path: window.location.pathname,
                page_location: window.location.href
              });
              
              // Track specific reasons for optimization
              if (reason === 'opt_out_or_no_session') {
                window.gtag('event', 'one_tap_no_google_session', {
                  event_category: 'User Behavior',
                  event_label: 'User Not Logged Into Google',
                  page_path: window.location.pathname
                });
              } else if (reason === 'suppressed_by_user') {
                window.gtag('event', 'one_tap_user_suppressed', {
                  event_category: 'User Behavior',
                  event_label: 'User Previously Dismissed',
                  page_path: window.location.pathname
                });
              }
            }
            
          } else if (notification.isSkippedMoment()) {
            const reason = notification.getSkippedReason();
            console.log('One Tap skipped:', reason);
            
            // Track skip with context
            if (window.gtag) {
              window.gtag('event', 'one_tap_skipped', {
                event_category: 'Authentication',
                event_label: reason,
                skip_reason: reason,
                page_path: window.location.pathname
              });
              
              // Specific tracking for user cancellation (important metric)
              if (reason === 'user_cancel') {
                window.gtag('event', 'one_tap_user_cancelled', {
                  event_category: 'User Behavior',
                  event_label: 'User Actively Cancelled',
                  page_path: window.location.pathname,
                  interaction_type: 'negative'
                });
              }
            }
            
          } else if (notification.isDismissedMoment()) {
            const reason = notification.getDismissedReason();
            console.log('One Tap dismissed:', reason);
            
            // Track dismissal (important for conversion funnel)
            if (window.gtag) {
              window.gtag('event', 'one_tap_dismissed', {
                event_category: 'Authentication',
                event_label: reason,
                dismissal_reason: reason,
                page_path: window.location.pathname
              });
              
              // Track successful completion vs user dismissal
              if (reason === 'credential_returned') {
                window.gtag('event', 'one_tap_completed', {
                  event_category: 'Conversion',
                  event_label: 'Sign In Process Complete',
                  page_path: window.location.pathname
                });
              } else if (reason === 'cancel') {
                window.gtag('event', 'one_tap_conversion_lost', {
                  event_category: 'Conversion',
                  event_label: 'User Cancelled During Sign In',
                  page_path: window.location.pathname,
                  funnel_step: 'dismissal'
                });
              }
            }
            
          } else {
            console.log('One Tap displayed successfully');
            
            // Track successful display (top of funnel)
            if (window.gtag) {
              window.gtag('event', 'one_tap_displayed', {
                event_category: 'Authentication',
                event_label: 'Success',
                page_path: window.location.pathname,
                page_location: window.location.href,
                funnel_step: 'display'
              });
              
              // Track impression for conversion rate calculation
              window.gtag('event', 'one_tap_impression', {
                event_category: 'User Acquisition',
                event_label: 'Popup Shown',
                page_path: window.location.pathname,
                popup_type: 'automatic'
              });
            }
          }
        });

        setInitialized(true);
        setInitializing(false);
        initializingRef.current = false;
        console.log("Google One Tap initialized successfully");

      } catch (error) {
        console.error("Error initializing Google One Tap:", error);
        setInitializing(false);
        initializingRef.current = false;
        if (onErrorRef.current) {
          onErrorRef.current(error);
        }
      }
    };

    // Start initialization
    initializeOneTap();

    // Cleanup
    return () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, [isClient, disabled, initialized, initializing, shouldShowOnCurrentPage, handleCredentialResponse]);

  // This component doesn't render anything visible in production or development
  // The One Tap UI is managed by Google's library
  return null;
}

export default GoogleOneTap;
